import { useEffect, useState } from "react";
import {
  createNativeUpdateGate,
  type MinimumBuildRequirement,
} from "./updateGate";

/**
 * React binding for the iOS minimum-build update gate (SD-19983).
 *
 * Mounted once by `App`, which swaps the router out for the blocking screen
 * while `required` is true. Instant no-op on web, the PWA and Android: the gate
 * checks the platform before it loads a plugin or touches the network.
 *
 * Two-way on purpose. `App` keeps this hook mounted while the screen is up, so
 * the gate's foreground listener survives the block and a lowered published
 * minimum flips `required` back to false, remounting the router — that is the
 * rollback path, and it is the reason nothing here is latched or persisted.
 */
const NOT_REQUIRED: MinimumBuildRequirement = { required: false };

export const useNativeUpdateRequired = (): MinimumBuildRequirement => {
  const [requirement, setRequirement] =
    useState<MinimumBuildRequirement>(NOT_REQUIRED);

  useEffect(() => {
    const gate = createNativeUpdateGate({
      onRequirementChange: setRequirement,
    });
    gate.start();
    return gate.stop;
  }, []);

  return requirement;
};
